import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage, languages } from '../../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const navLabels = {
  de: {
    leistungen: 'Leistungen',
    projekte: 'Projekte',
    team: 'Über uns',
    kontakt: 'Kontakt'
  },
  it: {
    leistungen: 'Servizi',
    projekte: 'Progetti',
    team: 'Chi siamo',
    kontakt: 'Contatto'
  },
  en: {
    leistungen: 'Services',
    projekte: 'Projects',
    team: 'About Us',
    kontakt: 'Contact'
  },
  es: {
    leistungen: 'Servicios',
    projekte: 'Proyectos',
    team: 'Sobre nosotros',
    kontakt: 'Contacto'
  }
};

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, changeLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const labels = navLabels[language] || navLabels.de;

  // Navigation items
  const navItems = [
    { href: '#leistungen', label: labels.leistungen },
    { href: '#projekte', label: labels.projekte },
    { href: '#team', label: labels.team },
    { href: '#kontakt', label: labels.kontakt },
  ];

  const scrollToSection = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      if (location.pathname !== '/') {
        window.location.href = '/' + href;
        return;
      }
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    }
  };

  const currentLang = languages.find(l => l.code === language);

  // Hide header on homepage hero, show after scroll
  if (location.pathname === '/' && !isScrolled) {
    return null;
  }

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#e5e5e5]"
      >
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <nav className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center group"
              data-testid="logo-link"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <span className="text-lg font-bold tracking-tight text-[#0a0a0a] font-['Manrope']">
                Easyweb
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="text-sm text-[#0a0a0a] hover:opacity-60 transition-opacity"
                  data-testid={`nav-${item.href.replace('#', '')}`}
                >
                  {item.label}
                </a>
              ))}
            </div>

            {/* Right side */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger 
                  className="flex items-center gap-2 text-sm text-[#0a0a0a] font-medium px-3 py-2"
                  data-testid="language-switcher"
                >
                  <span>{currentLang?.code.toUpperCase()}</span>
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border border-[#0a0a0a]">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`cursor-pointer ${
                        language === lang.code ? 'text-[#0a0a0a] font-bold' : 'text-[#0a0a0a]'
                      }`}
                      data-testid={`lang-${lang.code}`}
                    >
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* CTA Button */}
              <a
                href="#kontakt"
                onClick={(e) => scrollToSection(e, '#kontakt')}
                className="bg-[#0a0a0a] text-white px-6 py-2 text-sm font-medium hover:bg-[#333] transition-colors"
                data-testid="header-cta"
              >
                {labels.kontakt}
              </a>
            </div>

            {/* Mobile Language + Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Mobile Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger 
                  className="flex items-center gap-1 text-sm text-[#0a0a0a] font-medium px-2 py-2"
                  data-testid="mobile-language-switcher"
                >
                  <span>{currentLang?.code.toUpperCase()}</span>
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border border-[#0a0a0a]">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`cursor-pointer ${
                        language === lang.code ? 'text-[#0a0a0a] font-bold' : 'text-[#0a0a0a]'
                      }`}
                      data-testid={`mobile-lang-${lang.code}`}
                    >
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-[#0a0a0a]"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </nav>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-white lg:hidden"
          >
            <div className="flex flex-col pt-24 px-6">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="py-4 text-2xl font-bold text-[#0a0a0a] border-b border-[#0a0a0a] font-['Manrope']"
                >
                  {item.label}
                </motion.a>
              ))}

              {/* Mobile Language Switcher */}
              <div className="flex gap-4 mt-8">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`px-4 py-2 text-sm border border-[#0a0a0a] ${
                      language === lang.code
                        ? 'bg-[#0a0a0a] text-white'
                        : 'text-[#0a0a0a]'
                    }`}
                  >
                    {lang.code.toUpperCase()}
                  </button>
                ))}
              </div>

              {/* Mobile CTA */}
              <a
                href="#kontakt"
                onClick={(e) => scrollToSection(e, '#kontakt')}
                className="mt-8 bg-[#0a0a0a] text-white px-6 py-3 text-center font-medium"
              >
                {labels.kontakt}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
